/**
 * The project: everything about an edit except the media itself.
 *
 * Two rules hold this file together, and both exist because of decisions taken
 * before any of it was written:
 *
 *  1. It is plain data. No File objects, no DOM nodes, no class instances are
 *     reachable from a Project, so `structuredClone` and `JSON.stringify` both
 *     work on it as-is. Persistence and the downloadable project file are then
 *     a storage layer underneath rather than a rewrite.
 *  2. We never store media bytes. A source is a *reference*: enough to lay the
 *     timeline out, name the file we want back, and tell whether the file a
 *     person hands us later is the one the edit was built on.
 *
 * Every operation below returns a new Project rather than mutating one, which
 * is what makes undo a stack of snapshots later.
 */

import { autoTextHeight, DEFAULT_TEXT_STYLE, fittedTextWidth, type TextStyle } from "./text.ts";
import type { ClipAnimation } from "./animation.ts";

export const PROJECT_VERSION = 1;

/** Geometry is normalised to the composition, not to pixels, because the
 *  preview canvas and the export canvas are different sizes and the same
 *  numbers have to mean the same thing in both. */
/**
 * How a picture maps into its box once the box is no longer the picture's own
 * shape. Only meaningful while the aspect ratio is unlocked; with it locked the
 * box *is* the source's shape and all three are the same thing.
 */
export type FitMode = "cover" | "contain" | "stretch";

export interface Transform {
  /** Centre of the clip, 0..1 across the composition. */
  x: number;
  y: number;
  /** Width of the box as a fraction of composition width. 1 fills it. */
  scale: number;
  /**
   * Height of the box as a fraction of composition height.
   *
   * Absent means locked to the source's aspect ratio, which is the default and
   * what almost everyone wants; setting it unlocks the two axes so a clip can
   * be sized freely, and `fit` then decides what happens to the picture.
   */
  height?: number;
  fit: FitMode;
  /** Degrees, clockwise. */
  rotation: number;
  opacity: number;
  /** Rounded-corner radius as a fraction of the clip's shorter side. 0.5 is a circle. */
  radius: number;
}

export const IDENTITY_TRANSFORM: Transform = {
  x: 0.5,
  y: 0.5,
  scale: 1,
  fit: "cover",
  rotation: 0,
  opacity: 1,
  radius: 0,
};

export const aspectLocked = (transform: Transform): boolean => transform.height === undefined;

/**
 * Whether the box is still the picture's own shape.
 *
 * Two controls hang off this one question. The fit mode can only make a
 * difference when the answer is no — a 16:9 clip filling a 16:9 frame draws
 * the same pixels under all three modes — and there is only something to
 * restore when the answer is no as well.
 */
export function boxMatchesSource(
  project: Project,
  transform: Transform,
  source: SourceRef,
): boolean {
  const sourceAspect = sourceAspectOf(source);
  const boxAspect =
    (transform.scale * project.width) / (boxHeight(project, transform, source) * project.height);
  return Math.abs(boxAspect - sourceAspect) <= 0.01;
}

/** A source's own shape, or 16:9 when it has none we know of. */
export const sourceAspectOf = (source: SourceRef | undefined): number =>
  source?.width && source.height ? source.width / source.height : 16 / 9;

export const fitMatters = (project: Project, transform: Transform, source: SourceRef): boolean =>
  !boxMatchesSource(project, transform, source);

/**
 * Which position preset the clip is currently sitting on, if any.
 *
 * Lets the picker show where a clip is rather than only offering to move it,
 * and costs nothing: the presets are pure functions of the composition and the
 * source, so this is a comparison rather than remembered state.
 */
export function matchingPreset(
  project: Project,
  clip: Clip,
  source: SourceRef | undefined,
): PresetName | null {
  const names: PresetName[] = ["centre", "top-left", "top-right", "bottom-left", "bottom-right"];
  for (const name of names) {
    const target = presetForClip(name, project, clip, source, clip.transform.scale);
    const sameX = Math.abs((target.x ?? 0) - clip.transform.x) < 0.002;
    const sameY = Math.abs((target.y ?? 0) - clip.transform.y) < 0.002;
    if (sameX && sameY) return name;
  }
  return null;
}

/**
 * The three sizing states a clip can be in, which are exclusive and so belong
 * in one control rather than a toggle plus a button that quietly changes it.
 *
 *  - `locked`: the box follows the source's proportions.
 *  - `filled`: the box is the whole frame, whatever shape the picture is.
 *  - `free`: anything else, sized by hand.
 *
 * `filled` is a special case of `free` rather than a fourth kind of state, so
 * it is recognised by its geometry instead of being recorded separately. One
 * fewer thing that can disagree with the box it describes.
 */
export type SizeMode = "locked" | "filled" | "free";

const FILLED_TOLERANCE = 0.001;

export function sizeMode(transform: Transform): SizeMode {
  if (aspectLocked(transform)) return "locked";
  const fills =
    Math.abs(transform.scale - 1) < FILLED_TOLERANCE &&
    Math.abs((transform.height ?? 0) - 1) < FILLED_TOLERANCE &&
    Math.abs(transform.x - 0.5) < FILLED_TOLERANCE &&
    Math.abs(transform.y - 0.5) < FILLED_TOLERANCE;
  return fills ? "filled" : "free";
}

/**
 * Make the box the whole frame.
 *
 * Deliberately silent about the fit mode: a filled box with `contain` shows the
 * whole picture letterboxed inside the frame, which is a real thing to want,
 * and the fit control is right there for saying otherwise.
 */
export function fillFrame(project: Project, clipId: string): Project {
  return setTransform(project, clipId, { x: 0.5, y: 0.5, scale: 1, height: 1 });
}

export function setSizeMode(project: Project, clipId: string, mode: SizeMode): Project {
  if (mode === "filled") return fillFrame(project, clipId);
  return setAspectLocked(project, clipId, mode === "locked");
}

/**
 * Unlocking keeps the box exactly where it is by writing down the height it
 * already had, so the picture does not move at the moment of unlocking; only
 * what is then possible changes. Locking throws the height away and the box
 * snaps back to the source's shape.
 */
export function setAspectLocked(
  project: Project,
  clipId: string,
  locked: boolean,
): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip || !isVisual(clip.kind)) return project;
  const source = sourceById(project, clip.sourceId);
  if (clip.kind === "video" && !source) return project;
  if (locked) {
    const { height: _dropped, ...rest } = clip.transform;
    return updateClip(project, clipId, { transform: rest as Transform });
  }
  if (!aspectLocked(clip.transform)) return project;
  return setTransform(project, clipId, { height: clipBoxHeight(project, clip, source) });
}

/** Back to the source's own shape, keeping the width and the aspect unlocked. */
export function resetAspect(project: Project, clipId: string): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip || !isVisual(clip.kind)) return project;
  const source = sourceById(project, clip.sourceId);
  if (clip.kind === "video" && !source) return project;
  if (aspectLocked(clip.transform)) return project;
  const { height: _dropped, ...locked } = clip.transform;
  return setTransform(project, clipId, {
    height: clipBoxHeight(project, { ...clip, transform: locked as Transform }, source),
  });
}

const FIT_ORDER: FitMode[] = ["cover", "contain", "stretch"];

export function cycleFit(project: Project, clipId: string): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip) return project;
  const next = FIT_ORDER[(FIT_ORDER.indexOf(clip.transform.fit) + 1) % FIT_ORDER.length];
  return setTransform(project, clipId, { fit: next ?? "cover" });
}

/** Rotation in 90° steps, which is what a button is for. */
export function rotateClip(project: Project, clipId: string, degrees = 90): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip) return project;
  return setTransform(project, clipId, { rotation: clip.transform.rotation + degrees });
}

/** The box's height as a fraction of the composition, whether locked or not. */
export function boxHeight(project: Project, transform: Transform, source: SourceRef): number {
  if (transform.height !== undefined) return transform.height;
  return naturalHeight(project, transform.scale, source);
}

function naturalHeight(project: Project, scale: number, source: SourceRef | undefined): number {
  return (scale * project.width) / sourceAspectOf(source) / project.height;
}

/**
 * The box's height for any visual clip.
 *
 * A picture's "locked" height is its own proportions; a title's is however
 * tall the words come out at the box's width. Both are the answer to the same
 * question — what is this box's height when nobody has set one — so they are
 * one function, and the callers that lay out handles and presets do not have
 * to know which kind they are holding.
 */
export function clipBoxHeight(project: Project, clip: Clip, source: SourceRef | undefined): number {
  if (clip.transform.height !== undefined) return clip.transform.height;
  if (clip.kind === "text" && clip.text) {
    return autoTextHeight(clip.text.content, clip.text.style, project, clip.transform.scale);
  }
  return naturalHeight(project, clip.transform.scale, source);
}

/**
 * A file this project needs, described well enough to ask for it back.
 *
 * `fingerprint` hashes the first and last megabyte together with the size
 * rather than the whole file: a full hash of a 2 GB video costs ten seconds or
 * more, and this is specific enough to tell a re-export apart from the original.
 */
export interface SourceRef {
  id: string;
  name: string;
  size: number;
  lastModified: number;
  /** Seconds. Kept so the timeline still lays out when the file is missing. */
  duration: number;
  /** Natural pixel size, for video. Absent for audio-only files. */
  width?: number;
  height?: number;
  hasVideo: boolean;
  hasAudio: boolean;
  fingerprint: string;
  /** Poster frame as a data URL, for the project list. Never the media itself. */
  poster?: string;
  /**
   * Set when this source was made from another one here rather than loaded
   * from disk, and how. It cannot be relinked to a file; it is rebuilt from
   * its origin.
   */
  derived?: { from: string; op: "denoise" };
  /**
   * A still image. It has no clock: `duration` is only how long a clip of it
   * runs for when first laid down, and a clip can be trimmed out to any length.
   */
  still?: true;
}

/** How long a photo stays up when dropped on the timeline. */
export const STILL_DURATION = 5;
/** How long a new title stays up. */
export const TEXT_DURATION = 4;

/**
 * Video and text are both *visual*: they occupy a box in the frame, are drawn
 * by renderFrame and carry a transform. Text draws above every video lane,
 * which is where a title always wants to be, and it has no source at all —
 * the words live in the clip.
 */
export type ClipKind = "video" | "audio" | "text";

export const isVisual = (kind: ClipKind): boolean => kind !== "audio";

export interface TextContent {
  content: string;
  style: TextStyle;
}

export interface Clip {
  id: string;
  /** Empty for a text clip, which has no file behind it. */
  sourceId: string;
  kind: ClipKind;
  /** Lane within its kind. Higher video tracks draw on top; audio tracks all mix. */
  track: number;
  /** Where the clip starts on the timeline, in seconds. */
  start: number;
  /** In and out points within the source, in seconds. */
  in: number;
  out: number;
  /**
   * Linear gain, 1 being unchanged. **Audio clips only.**
   *
   * A video clip's sound is not its own: importing a file with audio creates a
   * separate audio clip over the same source, and the mixer and the exporter
   * both read the level from that one. These fields exist on every clip because
   * a clip is one shape, but nothing reads them for a video clip, so nothing
   * should offer to set them either.
   */
  volume: number;
  muted: boolean;
  /** Visual clips only; ignored for audio clips. */
  transform: Transform;
  /** Set on a cleaned copy: the id of the clip it was made from. */
  cleanOf?: string;
  /** Present on, and only on, a text clip. */
  text?: TextContent;
  /** How a visual clip arrives and leaves. Absent means it cuts. */
  animation?: ClipAnimation;
}

export interface Project {
  version: typeof PROJECT_VERSION;
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  /** Composition size and rate. Taken from the first video added unless chosen by hand. */
  width: number;
  height: number;
  fps: number;
  /**
   * Set once someone has picked a frame size themselves. Until then the
   * first video decides, and a project that was made 9:16 on purpose must
   * not turn landscape because a landscape clip was dropped in.
   */
  sizedByHand?: true;
  sources: SourceRef[];
  clips: Clip[];
}

export const DEFAULT_COMPOSITION = { width: 1920, height: 1080, fps: 30 };

let counter = 0;
/** Ids only have to be unique inside one project, and readable in a saved file. */
export function newId(prefix: string): string {
  counter += 1;
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}`;
}

export function emptyProject(name = "Untitled"): Project {
  const now = Date.now();
  return {
    version: PROJECT_VERSION,
    id: newId("p"),
    name,
    createdAt: now,
    updatedAt: now,
    ...DEFAULT_COMPOSITION,
    sources: [],
    clips: [],
  };
}

const touch = (project: Project, patch: Partial<Project>): Project => ({
  ...project,
  ...patch,
  updatedAt: Date.now(),
});

export const clipDuration = (clip: Clip): number => Math.max(0, clip.out - clip.in);
export const clipEnd = (clip: Clip): number => clip.start + clipDuration(clip);

/** Length of the whole edit: where the last clip ends. */
export function projectDuration(project: Project): number {
  return project.clips.reduce((longest, clip) => Math.max(longest, clipEnd(clip)), 0);
}

export function sourceById(project: Project, id: string): SourceRef | undefined {
  return project.sources.find((source) => source.id === id);
}

/** Clips covering `t`, in draw order: lowest track first, so later ones sit on top. */
export function clipsAt(project: Project, t: number, kind?: ClipKind): Clip[] {
  return project.clips
    .filter((clip) => (kind ? clip.kind === kind : true) && t >= clip.start && t < clipEnd(clip))
    .sort((a, b) => a.track - b.track || a.start - b.start);
}

/** Everything drawn at `t`, bottom to top: the video stack, then the text stack. */
export function visualClipsAt(project: Project, t: number): Clip[] {
  return [...clipsAt(project, t, "video"), ...clipsAt(project, t, "text")];
}

/** What to call a clip where a name is needed: its file, or its words. */
export function clipLabel(clip: Clip, source: SourceRef | undefined): string {
  if (clip.kind === "text") {
    const line = (clip.text?.content ?? "").split("\n").find((entry) => entry.trim()) ?? "";
    return line.trim() || "Empty text";
  }
  return source?.name ?? "missing file";
}

/** Where in the source file a clip is at timeline position `t`. */
export function sourceTimeAt(clip: Clip, t: number): number {
  return clip.in + (t - clip.start);
}

/**
 * Add a file to the project and lay it on the timeline.
 *
 * A video that carries audio becomes *two* clips over one source: a video clip
 * and an audio clip. That is what lets someone drop the music under a clip and
 * pull the original sound down without re-encoding anything, and it is why
 * Clip.sourceId is a reference rather than an owner.
 */
export function addSource(
  project: Project,
  source: SourceRef,
  options: { start?: number; videoTrack?: number; audioTrack?: number } = {},
): { project: Project; clips: Clip[] } {
  const start = options.start ?? 0;
  const added: Clip[] = [];

  if (source.hasVideo) {
    added.push({
      id: newId("c"),
      sourceId: source.id,
      kind: "video",
      track: options.videoTrack ?? nextFreeTrack(project, "video"),
      start,
      in: 0,
      out: source.duration,
      volume: 1,
      muted: false,
      transform: { ...IDENTITY_TRANSFORM },
    });
  }
  if (source.hasAudio) {
    added.push({
      id: newId("c"),
      sourceId: source.id,
      kind: "audio",
      track: options.audioTrack ?? nextFreeTrack(project, "audio"),
      start,
      in: 0,
      out: source.duration,
      volume: 1,
      muted: false,
      transform: { ...IDENTITY_TRANSFORM },
    });
  }

  // The first video decides the composition, so a portrait phone clip does not
  // land letterboxed inside a landscape frame nobody asked for. A still does
  // not: a photo dropped into an edit is an overlay, not the edit's shape. And
  // a size someone chose is theirs.
  const firstVideo =
    !project.clips.some((clip) => clip.kind === "video" && !sourceById(project, clip.sourceId)?.still) &&
    source.hasVideo &&
    !source.still;
  const composition =
    firstVideo && !project.sizedByHand && source.width && source.height
      ? { width: source.width, height: source.height }
      : {};

  // The first video into a frame someone chose fills that frame, cropped to
  // its shape: a landscape clip dropped into a Reel is meant to become the
  // Reel, not a strip across the middle of it.
  if (firstVideo && project.sizedByHand && source.width && source.height) {
    const frameAspect = project.width / project.height;
    if (Math.abs(frameAspect - source.width / source.height) > 0.01) {
      for (let i = 0; i < added.length; i += 1) {
        const clip = added[i];
        if (clip?.kind === "video") added[i] = { ...clip, transform: { ...clip.transform, height: 1 } };
      }
    }
  }

  return {
    project: touch(project, {
      ...composition,
      sources: [...project.sources, source],
      clips: [...project.clips, ...added],
    }),
    clips: added,
  };
}

/**
 * A title, laid down at `start` for a few seconds on a text lane of its own.
 *
 * The box is 80% of the frame wide and as tall as the words: wide enough that
 * a sentence wraps into something readable rather than a column, and the
 * inspector has a button for shrinking it to fit once the words are final.
 */
export function addTextClip(
  project: Project,
  options: { id?: string; start?: number; track?: number; content?: string; style?: TextStyle } = {},
): { project: Project; clip: Clip } {
  const clip: Clip = {
    id: options.id ?? newId("c"),
    sourceId: "",
    kind: "text",
    track: options.track ?? nextFreeTrack(project, "text"),
    start: Math.max(0, options.start ?? 0),
    in: 0,
    out: TEXT_DURATION,
    volume: 1,
    muted: false,
    transform: { ...IDENTITY_TRANSFORM, scale: 0.8, fit: "contain" },
    text: { content: options.content ?? "Your text here", style: options.style ?? DEFAULT_TEXT_STYLE },
  };
  return { project: touch(project, { clips: [...project.clips, clip] }), clip };
}

export function setTextContent(project: Project, clipId: string, content: string): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip?.text) return project;
  return updateClip(project, clipId, { text: { ...clip.text, content } });
}

export function setTextStyle(project: Project, clipId: string, patch: Partial<TextStyle>): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip?.text) return project;
  return updateClip(project, clipId, { text: { ...clip.text, style: { ...clip.text.style, ...patch } } });
}

/**
 * Shrink the box to its widest line, keeping its centre, and let the height
 * follow the words again. For a boxed caption this is what turns a bar the
 * width of the frame into a pill around the sentence.
 */
export function fitTextBox(project: Project, clipId: string): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip?.text) return project;
  const scale = Math.max(0.05, fittedTextWidth(clip.text.content, clip.text.style, project));
  return setTransform(project, clipId, { scale, height: undefined });
}

export function setAnimation(project: Project, clipId: string, patch: Partial<ClipAnimation>): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip || !isVisual(clip.kind)) return project;
  const base: ClipAnimation = clip.animation ?? { in: "none", out: "none", inDuration: 0.5, outDuration: 0.5 };
  return updateClip(project, clipId, { animation: { ...base, ...patch } });
}

/* ---- composition --------------------------------------------------------- */

export interface CompositionPreset {
  id: string;
  label: string;
  /** Where this shape is what the platform wants. */
  hint: string;
  width: number;
  height: number;
}

/**
 * The shapes people actually export to. Reels, TikTok, Shorts and every
 * story format take the same 1080×1920, so there is one vertical preset
 * rather than four that would all be the same numbers.
 */
export const COMPOSITION_PRESETS: CompositionPreset[] = [
  { id: "landscape", label: "Wide", hint: "Landscape · YouTube, 16:9", width: 1920, height: 1080 },
  { id: "vertical", label: "Tall", hint: "Vertical · Reels, TikTok, Shorts, Stories · 9:16", width: 1080, height: 1920 },
  { id: "square", label: "Square", hint: "Instagram and LinkedIn feeds · 1:1", width: 1080, height: 1080 },
  { id: "portrait", label: "Portrait", hint: "Instagram feed, tall · 4:5", width: 1080, height: 1350 },
  { id: "uhd", label: "4K", hint: "YouTube at 2160p · 16:9", width: 3840, height: 2160 },
  { id: "hd", label: "720p", hint: "Smaller files, 16:9", width: 1280, height: 720 },
];

export const MIN_SIDE = 16;
export const MAX_SIDE = 7680;

/** Encoders want even dimensions; people type odd ones. */
export const evenSide = (value: number): number =>
  Math.max(MIN_SIDE, Math.min(MAX_SIDE, Math.round(value / 2) * 2));

/**
 * Change the frame's size.
 *
 * Every transform is a fraction of the frame, so most of the edit comes along
 * unchanged: a clip in the bottom-right corner is still in the bottom-right
 * corner. The exception is a clip that was filling the frame. Its fractions
 * would keep it the width of the new frame at its own shape — a landscape
 * clip turned into a letterboxed strip across a vertical frame — when what
 * turning an edit vertical means is that the footage should still fill it.
 * Those clips are kept filling, and the fit mode decides what is cropped.
 */
export function setComposition(
  project: Project,
  size: { width: number; height: number },
): Project {
  return resizeFrame(touch(project, { sizedByHand: true }), size);
}

function resizeFrame(project: Project, size: { width: number; height: number }): Project {
  const width = evenSide(size.width);
  const height = evenSide(size.height);
  if (width === project.width && height === project.height) return project;
  const clips = project.clips.map((clip) => {
    if (clip.kind !== "video") return clip;
    const source = sourceById(project, clip.sourceId);
    if (!fillsFrame(project, clip, source)) return clip;
    return { ...clip, transform: { ...clip.transform, x: 0.5, y: 0.5, scale: 1, height: 1 } };
  });
  return touch(project, { width, height, clips });
}

/** Whether a clip's box covers the whole frame, at whatever shape. */
export function fillsFrame(project: Project, clip: Clip, source: SourceRef | undefined): boolean {
  const w = clip.transform.scale;
  const h = clipBoxHeight(project, clip, source);
  const left = clip.transform.x - w / 2;
  const top = clip.transform.y - h / 2;
  const slack = 0.002;
  return left <= slack && top <= slack && left + w >= 1 - slack && top + h >= 1 - slack;
}

/** The size the first real video would give the frame, if there is one. */
export function footageSize(project: Project): { width: number; height: number } | null {
  for (const clip of [...project.clips].sort((a, b) => a.track - b.track || a.start - b.start)) {
    if (clip.kind !== "video") continue;
    const source = sourceById(project, clip.sourceId);
    if (source && !source.still && source.width && source.height) {
      return { width: source.width, height: source.height };
    }
  }
  return null;
}

/** Back to letting the footage decide, and to its size if it is there. */
export function unsizeComposition(project: Project): Project {
  const footage = footageSize(project);
  const { sizedByHand: _dropped, ...rest } = project;
  const unsized = touch(rest as Project, {});
  return footage ? resizeFrame(unsized, footage) : unsized;
}

/** Which preset the frame is currently on, if any. */
export function matchingComposition(project: Project): CompositionPreset | null {
  return (
    COMPOSITION_PRESETS.find((preset) => preset.width === project.width && preset.height === project.height) ??
    null
  );
}

/**
 * A track of its own for each file added.
 *
 * The obvious rule is the lowest lane with room in it, which packs clips
 * tightly and is wrong: two unrelated files sharing a lane cannot be given
 * different levels or reordered without first being separated, and a person who
 * adds a music bed expects to see it appear as its own row rather than tucked
 * in after something else. One file, one lane. Clips split from an existing one
 * stay in the lane they came from, because they are the same footage.
 */
function nextFreeTrack(project: Project, kind: ClipKind): number {
  const used = project.clips.filter((clip) => clip.kind === kind);
  if (used.length === 0) return 0;
  return used.reduce((highest, clip) => Math.max(highest, clip.track), 0) + 1;
}

/** Highest lane in use for a kind, or -1 when there are none. */
export function topTrack(project: Project, kind: ClipKind): number {
  return project.clips
    .filter((clip) => clip.kind === kind)
    .reduce((highest, clip) => Math.max(highest, clip.track), -1);
}

export function updateClip(project: Project, clipId: string, patch: Partial<Clip>): Project {
  return touch(project, {
    clips: project.clips.map((clip) => (clip.id === clipId ? { ...clip, ...patch } : clip)),
  });
}

/**
 * Clamping happens here rather than at each call site.
 *
 * It used to be the caller's job, and `rotateClip` did not do it: four presses
 * of the rotate button left a rotation of 360 rather than 0, and it would have
 * gone on climbing. A patch that cannot reach an invalid value regardless of
 * who wrote it is one fewer thing for the next operation to remember.
 */
export function setTransform(
  project: Project,
  clipId: string,
  patch: Partial<Transform>,
): Project {
  const safe = clampTransform(patch);
  return touch(project, {
    clips: project.clips.map((clip) =>
      clip.id === clipId ? { ...clip, transform: { ...clip.transform, ...safe } } : clip,
    ),
  });
}

/**
 * Where an overlay goes when someone asks for a corner rather than dragging.
 *
 * Presets exist because dragging a reaction cam into a corner by hand is
 * fiddly and everyone wants the same five positions. `inset` is a fraction of
 * the composition, so the margin looks the same at any output size.
 */
export type PresetName = "centre" | "top-left" | "top-right" | "bottom-left" | "bottom-right";

export const OVERLAY_SCALE = 0.28;
const OVERLAY_INSET = 0.04;

export function presetTransform(
  name: PresetName,
  project: Project,
  source: SourceRef,
  scale = OVERLAY_SCALE,
): Partial<Transform> {
  // Half the clip's height as a fraction of the composition, so a wide clip
  // and a tall one both clear the edge by the same visible margin.
  return presetAt(name, scale, naturalHeight(project, scale, source) / 2);
}

/**
 * The same five places for any visual clip. A picture goes to a corner at its
 * own shape; a title goes there at the height its words need, which is what
 * `clipBoxHeight` answers for both.
 */
export function presetForClip(
  name: PresetName,
  project: Project,
  clip: Clip,
  source: SourceRef | undefined,
  scale: number,
): Partial<Transform> {
  const { height: _dropped, ...locked } = clip.transform;
  const probe: Clip = { ...clip, transform: { ...(locked as Transform), scale } };
  return presetAt(name, scale, clipBoxHeight(project, probe, source) / 2);
}

function presetAt(name: PresetName, scale: number, halfH: number): Partial<Transform> {
  // A preset also re-locks the aspect ratio: someone reaching for "bottom right"
  // wants the clip in the corner at its own shape, not their freehand stretch
  // moved into the corner.
  if (name === "centre") return { x: 0.5, y: 0.5, scale, height: undefined };
  const halfW = scale / 2;
  const left = name === "top-left" || name === "bottom-left";
  const top = name === "top-left" || name === "top-right";
  return {
    x: left ? OVERLAY_INSET + halfW : 1 - OVERLAY_INSET - halfW,
    y: top ? OVERLAY_INSET + halfH : 1 - OVERLAY_INSET - halfH,
    scale,
    height: undefined,
  };
}

export function applyPreset(project: Project, clipId: string, name: PresetName): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip || !isVisual(clip.kind)) return project;
  const source = sourceById(project, clip.sourceId);
  if (clip.kind === "video" && !source) return project;
  // A picture filling the frame shrinks to overlay size on its way to a
  // corner; a title keeps its width, since its size is its words.
  const scale =
    clip.kind === "video" && clip.transform.scale === 1 ? OVERLAY_SCALE : clip.transform.scale;
  return setTransform(project, clipId, presetForClip(name, project, clip, source, scale));
}

/**
 * Move a clip up or down the stack.
 *
 * Draw order is track order, so this is a track change rather than a separate
 * z-index — one number to keep consistent instead of two. Tracks are then
 * renumbered from zero so repeated nudges cannot drift into the hundreds.
 */
export function reorderClip(project: Project, clipId: string, direction: 1 | -1): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip) return project;
  const peers = project.clips
    .filter((c) => c.kind === clip.kind)
    .sort((a, b) => a.track - b.track);
  const index = peers.findIndex((c) => c.id === clipId);
  const swapWith = peers[index + direction];
  if (!swapWith) return project;

  const clips = project.clips.map((c) => {
    if (c.id === clip.id) return { ...c, track: swapWith.track };
    if (c.id === swapWith.id) return { ...c, track: clip.track };
    return c;
  });
  return touch(project, { clips: normaliseTracks(clips) });
}

/**
 * Whether moving this clip through the stack would change anything.
 *
 * The buttons ask before offering: a clip already on top has nowhere to go,
 * and a control that is enabled but inert is a worse answer than a disabled
 * one.
 */
export function canReorder(project: Project, clipId: string, direction: 1 | -1): boolean {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip) return false;
  const peers = project.clips
    .filter((c) => c.kind === clip.kind)
    .sort((a, b) => a.track - b.track);
  const index = peers.findIndex((c) => c.id === clipId);
  return index >= 0 && peers[index + direction] !== undefined;
}

/** Tracks renumbered 0..n per kind, keeping relative order. */
function normaliseTracks(clips: Clip[]): Clip[] {
  const out = [...clips];
  for (const kind of ["video", "audio", "text"] as ClipKind[]) {
    const order = [...new Set(out.filter((c) => c.kind === kind).map((c) => c.track))].sort(
      (a, b) => a - b,
    );
    for (let i = 0; i < out.length; i += 1) {
      const clip = out[i];
      if (!clip || clip.kind !== kind) continue;
      out[i] = { ...clip, track: order.indexOf(clip.track) };
    }
  }
  return out;
}

/**
 * Keep a clip reachable.
 *
 * A transform dragged fully off the composition leaves a clip that is still
 * there, still exported, and impossible to grab again. Clamping the centre to
 * the frame means the worst case is a clip mostly off the edge, which is a
 * legitimate thing to want and still has a handle on screen.
 */
export function clampTransform(patch: Partial<Transform>): Partial<Transform> {
  // `height: undefined` is meaningful — it re-locks the aspect ratio — so the
  // key is preserved rather than dropped.
  const out = { ...patch };
  // The centre stays inside the frame, which is what makes a clip impossible to
  // lose: however far it is pushed, at least its middle is on screen and under
  // the pointer. Anything looser and a clip can be dragged into nowhere and
  // only recovered by knowing it is still selectable from the timeline.
  if (out.x !== undefined) out.x = clamp(out.x, 0, 1);
  if (out.y !== undefined) out.y = clamp(out.y, 0, 1);
  if (out.scale !== undefined) out.scale = clamp(out.scale, 0.05, 4);
  if (out.height !== undefined) out.height = clamp(out.height, 0.05, 4);
  if (out.opacity !== undefined) out.opacity = clamp(out.opacity, 0, 1);
  if (out.radius !== undefined) out.radius = clamp(out.radius, 0, 0.5);
  if (out.rotation !== undefined) out.rotation = ((out.rotation % 360) + 360) % 360;
  return out;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Positions worth snapping to while dragging: the centre lines and the four
 * preset insets. Returned as raw coordinates so the caller can decide the
 * threshold in screen pixels rather than in composition fractions.
 */
export function snapTargets(
  project: Project,
  clip: Clip,
  source: SourceRef | undefined,
): { x: number[]; y: number[] } {
  const corner = presetForClip("top-left", project, clip, source, clip.transform.scale);
  return {
    x: [0.5, corner.x ?? 0.5, 1 - (corner.x ?? 0.5)],
    y: [0.5, corner.y ?? 0.5, 1 - (corner.y ?? 0.5)],
  };
}

/**
 * The audio clip carrying this video clip's sound, if the file had any.
 *
 * They are linked only by sharing a source, which is what lets one be levelled
 * or deleted without touching the other — and why the video clip needs to be
 * able to point at where its sound went.
 */
export function soundFor(project: Project, videoClip: Clip): Clip | undefined {
  return project.clips.find(
    (clip) => clip.kind === "audio" && clip.sourceId === videoClip.sourceId,
  );
}

/** Every clip in one lane, in timeline order. */
export function clipsInTrack(project: Project, kind: ClipKind, track: number): Clip[] {
  return project.clips
    .filter((clip) => clip.kind === kind && clip.track === track)
    .sort((a, b) => a.start - b.start);
}

/**
 * A lane's controls act on everything in it.
 *
 * A track is the unit people reach for — "turn the music down" means the music
 * track, not the third clip on it — and after a split one lane holds several
 * clips that should still move together.
 */
export function setTrackVolume(
  project: Project,
  kind: ClipKind,
  track: number,
  volume: number,
): Project {
  return touch(project, {
    clips: project.clips.map((clip) =>
      clip.kind === kind && clip.track === track ? { ...clip, volume } : clip,
    ),
  });
}

export function setTrackMuted(
  project: Project,
  kind: ClipKind,
  track: number,
  muted: boolean,
): Project {
  return touch(project, {
    clips: project.clips.map((clip) =>
      clip.kind === kind && clip.track === track ? { ...clip, muted } : clip,
    ),
  });
}

/**
 * Delete a lane and close the gap.
 *
 * Unlike deleting a clip, this renumbers: asking for a track to go and being
 * left with an empty row where it was is not what the button says.
 */
export function removeTrack(project: Project, kind: ClipKind, track: number): Project {
  const clips = project.clips.filter((clip) => !(clip.kind === kind && clip.track === track));
  const stillUsed = new Set(clips.map((clip) => clip.sourceId));
  return touch(project, {
    clips: normaliseTracks(clips),
    sources: project.sources.filter((source) => stillUsed.has(source.id)),
  });
}

export function removeClip(project: Project, clipId: string): Project {
  const clips = project.clips.filter((clip) => clip.id !== clipId);
  return touch(project, { clips, sources: usedSources(project, clips) });
}

/**
 * The sources some clip still points at, plus whatever those were derived
 * from: a cleaned track's original stays so the cleaning can be undone.
 */
function usedSources(project: Project, clips: Clip[]): SourceRef[] {
  const used = new Set(clips.map((clip) => clip.sourceId));
  for (const source of project.sources) {
    if (used.has(source.id) && source.derived) used.add(source.derived.from);
  }
  return project.sources.filter((source) => used.has(source.id));
}

/**
 * Put a cleaned copy of an audio clip on a new track directly beneath it, same
 * position, trim and level, and mute the original. Both stay: the original is
 * the way back, and hearing the two against each other is how you judge it.
 */
export function addCleanCopy(project: Project, clipId: string, source: SourceRef): Project {
  const clip = project.clips.find((entry) => entry.id === clipId);
  if (!clip || clip.kind !== "audio") return project;
  const below = clip.track + 1;
  const clips = project.clips.map((entry) => {
    if (entry.id === clipId) return { ...entry, muted: true };
    if (entry.kind === "audio" && entry.track >= below) return { ...entry, track: entry.track + 1 };
    return entry;
  });
  const copy: Clip = { ...clip, id: newId("c"), sourceId: source.id, track: below, muted: false, cleanOf: clip.id };
  const sources = project.sources.some((entry) => entry.id === source.id)
    ? project.sources
    : [...project.sources, source];
  return touch(project, { clips: [...clips, copy], sources });
}

/** The cleaned copy made from this clip, if it is still on the timeline. */
export function cleanCopyOf(project: Project, clip: Clip): Clip | undefined {
  return project.clips.find((entry) => entry.cleanOf === clip.id);
}

/**
 * Undo `addCleanCopy`, from either clip: the copy goes, the original is
 * unmuted, and the lane the copy had to itself closes up again.
 */
export function removeCleanCopy(project: Project, clipId: string): Project {
  const clip = project.clips.find((entry) => entry.id === clipId);
  if (!clip) return project;
  const copy = clip.cleanOf ? clip : cleanCopyOf(project, clip);
  if (!copy) return project;
  const originalId = copy.cleanOf;
  let clips = project.clips
    .filter((entry) => entry.id !== copy.id)
    .map((entry) => (entry.id === originalId ? { ...entry, muted: false } : entry));
  if (!clips.some((entry) => entry.kind === "audio" && entry.track === copy.track)) {
    clips = clips.map((entry) =>
      entry.kind === "audio" && entry.track > copy.track ? { ...entry, track: entry.track - 1 } : entry,
    );
  }
  return touch(project, { clips, sources: usedSources(project, clips) });
}

/**
 * Cut a clip in two at timeline position `t`. Both halves keep the same source
 * and the same transform, so splitting a laid-out picture-in-picture does not
 * move it.
 *
 * A cut outside the clip, or exactly on either edge, is not a cut: returning
 * the project unchanged is better than leaving a zero-length clip behind.
 */
export function splitClip(project: Project, clipId: string, t: number): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip) return project;
  const offset = t - clip.start;
  if (offset <= 0 || offset >= clipDuration(clip)) return project;

  const left: Clip = { ...clip, out: clip.in + offset };
  const right: Clip = {
    ...clip,
    id: newId("c"),
    start: clip.start + offset,
    in: clip.in + offset,
  };
  return touch(project, {
    clips: project.clips.flatMap((c) => (c.id === clipId ? [left, right] : [c])),
  });
}

/**
 * Move a clip's in or out point. Trimming the head moves `start` with it so the
 * remaining footage stays where it is on the timeline, which is what dragging
 * the left edge of a clip is expected to do.
 */
export function trimClip(
  project: Project,
  clipId: string,
  edge: "in" | "out",
  sourceTime: number,
): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip) return project;
  const source = sourceById(project, clip.sourceId);
  // A photo or a title has no end to run into: it stays up as long as asked.
  const timeless = clip.kind === "text" || source?.still;
  const limit = timeless ? Number.POSITIVE_INFINITY : (source?.duration ?? clip.out);
  const MIN = 1 / 60;

  if (edge === "in") {
    const next = Math.min(Math.max(0, sourceTime), clip.out - MIN);
    return updateClip(project, clipId, {
      in: next,
      start: Math.max(0, clip.start + (next - clip.in)),
    });
  }
  const next = Math.max(Math.min(limit, sourceTime), clip.in + MIN);
  return updateClip(project, clipId, { out: next });
}

/** Slide a clip along the timeline, or to another lane. Never before zero. */
export function moveClip(
  project: Project,
  clipId: string,
  start: number,
  track?: number,
): Project {
  const clip = project.clips.find((c) => c.id === clipId);
  if (!clip) return project;
  return updateClip(project, clipId, {
    start: Math.max(0, start),
    track: track ?? clip.track,
  });
}

/** Sources this project wants but has no file for. Drives the re-supply UI. */
export function missingSourceIds(project: Project, resolved: Set<string>): string[] {
  return project.sources.map((source) => source.id).filter((id) => !resolved.has(id));
}

/**
 * Round-trip a project through text. Reading validates the shape rather than
 * trusting it: this is the format people can hand each other, so a file from a
 * newer build has to fail with something a person can act on.
 */
export function serializeProject(project: Project): string {
  return JSON.stringify({ format: "otterstudio", ...project }, null, 2);
}

export function parseProject(text: string): Project {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error("That file isn't valid JSON.");
  }
  if (!raw || typeof raw !== "object") throw new Error("That file isn't an OtterStudio project.");
  const candidate = raw as Record<string, unknown>;
  if (candidate["format"] !== "otterstudio") {
    throw new Error("That file isn't an OtterStudio project.");
  }
  if (candidate["version"] !== PROJECT_VERSION) {
    throw new Error(
      `That project was saved by a different version of OtterStudio (found ${String(
        candidate["version"],
      )}, this build reads ${PROJECT_VERSION}).`,
    );
  }
  if (!Array.isArray(candidate["clips"]) || !Array.isArray(candidate["sources"])) {
    throw new Error("That project file is missing its clips or sources.");
  }
  const { format: _format, ...project } = candidate;
  return project as unknown as Project;
}
